import React, { FC, ReactElement, useContext, useState } from 'react'
import { AddUserLanguage } from '../../../i18n'
// Utilities imports.
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { getCMEIDFromTelSys, getPartitionInTelSysFromGUID } from '../Common/API/ItemGetters'
// Commons imports.
import { Modal } from '../../ReactRouting/Components/Molecules/Modal/Modal'
import { DocTopic } from '../Common/Constants/Documentation'
// Sub imports.
// Contexts imports.
import {
    LanguageContextProvider,
    MessageContextProvider,
    PromptContextProvider,
    ReactState,
    UserInfoContextProvider
} from '../Common/Contexts'
import { CommonFormContextProvider } from '../Common/Contexts/CommonFormContext'
import UserInfoContext, { UserInfoContextType } from '../Common/Contexts/UserInfoContext'
import { clearModalHelpTopic, setModalHelpTopicID } from '../Common/Utils/HelpPortal'
import { AddUserLoading } from './AddUserLoading'
import AddUserTabs from './AddUserTabs'
import { DEFAULT_CME_ID } from './Constants'
import { $window } from '../../NonReact/common/AkixiWindow'
import { FilterValues, InitialApplicationUserValues, PreferredReportingAccessLevel } from './Types'

type Props = {
    refreshAction: () => void
    filterCMEID?: string
    filterTelSysID?: number
    filterPartitionID?: string
    initialFilters?: FilterValues
    initialValues?: InitialApplicationUserValues
    preferredReportingAccessLevels?: PreferredReportingAccessLevel[]
    selectedUser?: string
    viewOnly?: boolean
    /** Optional callback invoked after the modal has fully exited (for parent-controlled mounting). */
    onModalExited?: () => void
}

type AddUserTabsContainerProps = {
    viewOnly?: boolean
    closeModal: () => void
    filterCMEID?: string
    filterTelSysID?: number
    filterPartitionID?: string
    initialFilters?: FilterValues
    initialValues?: InitialApplicationUserValues
    preferredReportingAccessLevels?: PreferredReportingAccessLevel[]
    selectedUser?: string
    refreshAction: () => void
}

// Required for userInfo context loading.
const AddUserTabsContainer: FC<AddUserTabsContainerProps> = (props: AddUserTabsContainerProps): ReactElement => {
    const userInfo: UserInfoContextType = useContext(UserInfoContext)
    const [partitionID, setPartitionID]: ReactState<Nullable<number>> = useState<Nullable<number>>(
        props.initialFilters?.partitionID ?? null
    )
    const [cmeID, setCmeID]: ReactState<Nullable<string>> = useState<Nullable<string>>(
        props.initialFilters?.cmeID ?? null
    )

    const { filterCMEID, filterTelSysID, filterPartitionID, initialFilters, initialValues }: AddUserTabsContainerProps =
        props

    if (cmeID === null) {
        getCMEIDFromTelSys(filterTelSysID, filterCMEID).then(setCmeID)
    }

    if (partitionID === null) {
        getPartitionInTelSysFromGUID(filterPartitionID).then(setPartitionID)
    }

    return userInfo && partitionID !== null && cmeID !== null ? (
        <AddUserTabs
            {...props}
            filters={{
                cmeID: cmeID ?? DEFAULT_CME_ID,
                telSysID: initialFilters?.telSysID ?? filterTelSysID,
                partitionID: partitionID ?? undefined,
                includeCmeChildren: initialFilters?.includeCmeChildren
            }}
            initialValues={initialValues}
        />
    ) : (
        <AddUserLoading />
    )
}

/**
 * Add User Modal.
 *
 * @param selectedUser - The user for edit.
 * @param refreshAction - The function used to refresh the list.
 */
const AddUserModal: FC<Props> = ({
    selectedUser,
    filterCMEID,
    filterTelSysID,
    filterPartitionID,
    initialFilters,
    initialValues,
    preferredReportingAccessLevels,
    refreshAction,
    viewOnly,
    onModalExited
}: Props): ReactElement => {
    // Access language resources directly without context as this is top level component.
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    language.loadBundle('AddUserLanguage')

    // Whether the modal window is open.
    const [isModalOpen, setModalOpen] = useState<boolean>(true)

    const closeModal = (): void => {
        $window.disableHotKeys = false
        // Component is controlled externally.
        setModalOpen(false)
    }

    return (
        <div>
            <LanguageContextProvider>
                <PromptContextProvider>
                    <MessageContextProvider>
                        <CommonFormContextProvider>
                            <Modal
                                isOpen={isModalOpen}
                                closeOnBackdropClick={true}
                                isClosableWithXButton={true}
                                inlineExitPromptSettings={{
                                    message: language.get(AddUserLanguage.AddUserLanguage_PromptMessage),
                                    confirmButtonText: language.get(AddUserLanguage.AddUserLanguage_PromptConfirm),
                                    cancelButtonText: language.get(AddUserLanguage.AddUserLanguage_PromptCancel),
                                    isRouterMode: true
                                }}
                                onOpen={(): void => {
                                    // Add contextual doc topic ID to the window object which is used
                                    // in old JSP logic that handles F1 key press.
                                    setModalHelpTopicID(DocTopic.ADMINAPPUSERSPROPS)
                                }}
                                onClose={closeModal}
                                onExited={(): void => {
                                    // Remove the contextual doc topic ID from the window that is used
                                    // in old JSP logic that handles F1 key press.
                                    // When removed the previous logic will work and the modal window doc topic will be not considered.
                                    clearModalHelpTopic()
                                    onModalExited?.()
                                }}
                            >
                                {isModalOpen ? (
                                    <UserInfoContextProvider>
                                        <div className='modal-title-report-config'>
                                            {selectedUser
                                                ? language.get(AddUserLanguage.Modifyusertitle)
                                                : language.get(AddUserLanguage.Addusertitle)}
                                        </div>
                                        <div className='user-add-modify'>
                                            <AddUserTabsContainer
                                                filterCMEID={filterCMEID}
                                                filterTelSysID={filterTelSysID}
                                                filterPartitionID={filterPartitionID}
                                                initialFilters={initialFilters}
                                                initialValues={initialValues}
                                                preferredReportingAccessLevels={preferredReportingAccessLevels}
                                                viewOnly={viewOnly}
                                                closeModal={closeModal}
                                                selectedUser={selectedUser}
                                                refreshAction={refreshAction}
                                            />
                                        </div>
                                    </UserInfoContextProvider>
                                ) : (
                                    <> </>
                                )}
                            </Modal>
                        </CommonFormContextProvider>
                    </MessageContextProvider>
                </PromptContextProvider>
            </LanguageContextProvider>
        </div>
    )
}

export default AddUserModal
